import type { TableRow } from "@/components/ui/standardized-table";
import type { SeoKeywordItem } from "@/backend-sdk/client/types.gen";

/**
 * <M-232> A tradução de uma linha de `palavras_chaves_table` para as chaves que a tabela
 * PALAVRAS CHAVES (SEARCH CONSOLE) desenha — e o rodapé pela mesma função.
 *
 * A tabela batiza as colunas em português (`impressoes`, `posicaoMedia`, `clicksChange`…) e a
 * resposta as publica com outros nomes (`impressoes_change_percentage`,
 * `siteCTR_change_percentage`…). Enquanto a página só desenhava linhas, a tradução podia
 * morar embutida no `useMemo`; a partir do momento em que o "Total Geral" passa a vir do
 * servidor, ela precisa ser **a mesma** para a linha e para o rodapé — traduzir o rodapé por
 * outro caminho o deixaria vazio na tela sem que nada falhasse, que é o defeito que M-225
 * encontrou nas três tabelas da Análise de Ticket Médio e que M-230 evitou pelo mesmo meio.
 *
 * Ela mora aqui, e não no módulo da página, porque uma regra dentro do módulo da página não
 * tem como ser testada sem desenhar a página inteira, e exportá-la de lá acenderia
 * `react-refresh/only-export-components`.
 */

/** O número, ou `null` — que a tabela desenha como `N/D`. */
const numeroOuNulo = (valor: unknown): number | null =>
  typeof valor === "number" && !Number.isNaN(valor) ? valor : null;

/** O mesmo, com duas casas — as duas colunas que a resposta publica com mais. */
const duasCasas = (valor: unknown): number | null => {
  const numero = numeroOuNulo(valor);
  return numero === null ? null : parseFloat(numero.toFixed(2));
};

export interface KeywordTableRow extends TableRow {
  query: string;
  clicks: number | null;
  clicksChange: number | null;
  impressoes: number | null;
  impressoesChange: number | null;
  siteCTR: number | null;
  siteCTRChange: number | null;
  posicaoMedia: number | null;
  posicaoMediaChange: number | null;
}

export const linhaDePalavraChave = (row: SeoKeywordItem): KeywordTableRow => ({
  query: row.query,
  clicks: numeroOuNulo(row.clicks),
  clicksChange: numeroOuNulo(row.clicks_change_percentage),
  impressoes: numeroOuNulo(row.impressoes),
  impressoesChange: numeroOuNulo(row.impressoes_change_percentage),
  siteCTR: duasCasas(row.siteCTR),
  siteCTRChange: numeroOuNulo(row.siteCTR_change_percentage),
  posicaoMedia: duasCasas(row.posicaoMedia),
  posicaoMediaChange: numeroOuNulo(row.posicaoMedia_change_percentage),
});
